<script setup>
import { computed, ref, watch } from 'vue';
import { PlayIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
    showPlayButton: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    isAdPlaying: { type: Boolean, default: false },
    adDuration: { type: Number, default: 0 },
    adCurrentTime: { type: Number, default: 0 },
    canSkipAd: { type: Boolean, default: false },
});

const emit = defineEmits(['play', 'toggle-play', 'skip-ad', 'ad-click']);

// Add delay before showing play button to prevent flashing during transitions
const showPlayButtonDelayed = ref(false);
const showLoadingDelayed = ref(false);
let playButtonTimeout = null;
let loadingTimeout = null;

watch(() => props.showPlayButton, (show) => {
    if (playButtonTimeout) {
        clearTimeout(playButtonTimeout);
        playButtonTimeout = null;
    }

    if (show) {
        // Delay showing play button by 1000ms to prevent flash during quick transitions
        playButtonTimeout = setTimeout(() => {
            showPlayButtonDelayed.value = true;
        }, 1000);
    } else {
        // Hide immediately when video starts playing
        showPlayButtonDelayed.value = false;
    }
});

watch(() => props.isLoading, (loading) => {
    if (loadingTimeout) {
        clearTimeout(loadingTimeout);
        loadingTimeout = null;
    }

    if (loading) {
        // Delay showing loading spinner by 800ms to prevent flash during quick loads
        loadingTimeout = setTimeout(() => {
            showLoadingDelayed.value = true;
        }, 800);
    } else {
        // Hide immediately when loading finishes
        showLoadingDelayed.value = false;
    }
});

// Computed properties
const adRemainingTime = computed(() => {
    const remaining = Math.max(0, props.adDuration - props.adCurrentTime);
    const minutes = Math.floor(remaining / 60);
    const seconds = Math.floor(remaining % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
});

const showAdTimer = computed(() => {
    return props.isAdPlaying && props.adDuration > 0;
});
</script>

<template>
    <div class="absolute inset-0 pointer-events-none">
        <!-- Clickable area for pause/play when video is playing (no play button visible) -->
        <div
            v-if="!showPlayButtonDelayed && !isLoading && !isAdPlaying"
            class="absolute inset-0 cursor-pointer pointer-events-auto"
            @click="emit('toggle-play')"
        ></div>

        <!-- Loading Spinner with delay to prevent flashing -->
        <Transition name="fade">
            <div
                v-if="showLoadingDelayed && !isAdPlaying"
                class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 pointer-events-none"
            >
                <div class="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
        </Transition>

        <!-- Play Button with delay to prevent flashing -->
        <Transition name="fade">
            <div
                v-if="showPlayButtonDelayed && !isLoading && !isAdPlaying"
                class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 cursor-pointer pointer-events-auto"
                @click="emit('play')"
            >
                <button type="button" class="focus:outline-none appearance-none p-2">
                    <PlayIcon class="w-20 h-20 text-white opacity-80 hover:opacity-100 transition-opacity"/>
                </button>
            </div>
        </Transition>

        <!-- Ad Overlay -->
        <div v-if="isAdPlaying" class="pointer-events-auto exclude-poppit">
            <!-- Clickable Ad Area -->
            <div 
                class="absolute inset-0 cursor-pointer exclude-poppit"
                @click.stop="emit('ad-click')"
            >
                <!-- CTA Button -->
                <div class="absolute bottom-20 left-1/2 transform -translate-x-1/2 pointer-events-none">
                    <button 
                        type="button" 
                        class="px-4 py-2 bg-white text-black rounded-full text-sm font-semibold shadow-lg hover:bg-gray-100 transition-colors pointer-events-auto exclude-poppit"
                        @click.stop="emit('ad-click')"
                    >
                        Learn More
                    </button>
                </div>
            </div>

            <!-- Skip Button -->
            <div 
                v-if="canSkipAd" 
                class="absolute bottom-4 right-4 z-10 exclude-poppit"
            >
                <button 
                    @click.stop="emit('skip-ad')" 
                    class="px-2 py-1 bg-black bg-opacity-50 text-white rounded text-sm hover:bg-opacity-70 transition-opacity exclude-poppit"
                    type="button"
                >
                    Skip Ad
                </button>
            </div>

            <!-- Ad Timer -->
            <div 
                v-if="showAdTimer"
                class="absolute bottom-4 left-4 text-white bg-black bg-opacity-50 px-2 py-1 rounded text-sm pointer-events-none"
            >
                Ad | {{ adRemainingTime }}
            </div>
        </div>

    </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.animate-spin {
    animation: spin 1s linear infinite;
}
</style>